import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  createFeature,
  deleteFeature,
  getFeatureBySlug,
  queryFeatures,
  updateFeature,
} from '@/features/features/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import {
  gridPaths,
  invalidateFeatureDetail,
  invalidateFeatures,
  invalidateGridQuery,
  invalidateReleaseDetails,
  invalidateReleases,
  queryKeys,
  useAccessToken,
  useInvalidateQueries,
  useOrganisationEntitlements,
} from '@/shared/hooks';
import { removeFromResult, upsertIntoResult } from '@/shared/hooks/shared';
import type { ApiError } from '@/shared/data/client';
import type { FeatureDto, QueryResult } from '@flowqa/shared';

type CreateFeatureInput = {
  name: string;
  projectId: string;
  status?: FeatureDto['status'];
  releaseId?: string | null;
  plannedFor?: string | null;
  releasedAt?: string | null;
  tags?: string[];
};
type UpdateFeatureInput = {
  id: string;
  name?: string;
  status?: FeatureDto['status'];
  projectId?: string;
  releaseId?: string | null;
  plannedFor?: string | null;
  releasedAt?: string | null;
  tags?: string[];
};
type DeleteFeatureInput = { id: string; projectId?: string };

const parseOptionalDate = (value: string | null | undefined): Date | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.valueOf()) ? null : parsed;
};

export const useFeaturesQuery = (
  projectId?: string | null,
): UseQueryResult<QueryResult<FeatureDto>, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const effectiveProjectId = projectId ?? selectedProjectId ?? null;
  const orgKey = selectedOrganisationId ?? null;
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const deliveryReady = selectedOrganisationId === null || entitlementsQuery.isSuccess;
  const deliveryEnabled =
    selectedOrganisationId === null
      ? true
      : (entitlementsQuery.data?.addOns.deliveryEnabled ?? false);
  const deliveryKey = deliveryEnabled ? 'delivery-on' : 'delivery-off';
  return useQuery<QueryResult<FeatureDto>, ApiError>({
    queryKey: queryKeys.features(effectiveProjectId, orgKey, deliveryKey),
    enabled: Boolean(token) && deliveryReady && deliveryEnabled,
    queryFn: async (): Promise<QueryResult<FeatureDto>> =>
      await queryFeatures(effectiveProjectId, selectedOrganisationId ?? undefined),
    staleTime: 30_000,
  });
};

export const useFeatureDetailQuery = (
  slug: string | null,
  projectId?: string | null,
): UseQueryResult<FeatureDto | null, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  const effectiveProjectId = projectId ?? selectedProjectId ?? null;
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const deliveryReady = selectedOrganisationId === null || entitlementsQuery.isSuccess;
  const deliveryEnabled =
    selectedOrganisationId === null
      ? true
      : (entitlementsQuery.data?.addOns.deliveryEnabled ?? false);
  const deliveryKey = deliveryEnabled ? 'delivery-on' : 'delivery-off';
  return useQuery<FeatureDto | null, ApiError>({
    queryKey: queryKeys.featureDetail(slug, orgKey, effectiveProjectId, deliveryKey),
    enabled: Boolean(token && slug) && deliveryReady && deliveryEnabled,
    queryFn: async (): Promise<FeatureDto | null> =>
      await getFeatureBySlug(slug, effectiveProjectId, selectedOrganisationId ?? undefined),
    staleTime: 15_000,
  });
};

export const useCreateFeature = (): UseMutationResult<FeatureDto, ApiError, CreateFeatureInput> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useMutation<FeatureDto, ApiError, CreateFeatureInput>({
    mutationFn: async (input: CreateFeatureInput): Promise<FeatureDto> => {
      return await createFeature(input, selectedOrganisationId ?? undefined);
    },
    onSuccess: async (created, variables) => {
      const allKey = queryKeys.features(undefined, orgKey);
      const projectKey = queryKeys.features(variables.projectId, orgKey);
      queryClient.setQueryData<QueryResult<FeatureDto>>(allKey, (prev) =>
        upsertIntoResult(prev, created),
      );
      queryClient.setQueryData<QueryResult<FeatureDto>>(projectKey, (prev) =>
        upsertIntoResult(prev, created),
      );
      await invalidateQueries([
        invalidateFeatures(undefined, orgKey),
        invalidateFeatures(variables.projectId, orgKey),
        invalidateGridQuery(gridPaths.features),
      ]);
    },
  });
};

export const useUpdateFeature = (): UseMutationResult<
  FeatureDto,
  ApiError,
  UpdateFeatureInput,
  {
    previousAll?: QueryResult<FeatureDto>;
    previousProject?: QueryResult<FeatureDto>;
    previousProjectId?: string;
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const allKey = queryKeys.features(undefined, orgKey);
  const projectKey = (projectId: string | undefined): ReturnType<typeof queryKeys.features> =>
    queryKeys.features(projectId ?? null, orgKey);

  return useMutation<
    FeatureDto,
    ApiError,
    UpdateFeatureInput,
    {
      previousAll?: QueryResult<FeatureDto>;
      previousProject?: QueryResult<FeatureDto>;
      previousProjectId?: string;
    }
  >({
    mutationFn: async (input: UpdateFeatureInput): Promise<FeatureDto> => {
      return await updateFeature(input, selectedOrganisationId);
    },
    onMutate: (input) => {
      const previousAll = queryClient.getQueryData<QueryResult<FeatureDto>>(allKey);
      const previousProjectId = input.projectId ?? undefined;
      const previousProject = previousProjectId
        ? queryClient.getQueryData<QueryResult<FeatureDto>>(projectKey(previousProjectId))
        : undefined;
      const optimisticInput = {
        ...input,
        plannedFor: parseOptionalDate(input.plannedFor),
        releasedAt: parseOptionalDate(input.releasedAt),
      };
      queryClient.setQueryData<QueryResult<FeatureDto>>(allKey, (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((item) =>
                item.id === input.id ? { ...item, ...optimisticInput } : item,
              ),
            }
          : prev,
      );
      if (previousProject && input.projectId) {
        queryClient.setQueryData<QueryResult<FeatureDto>>(projectKey(input.projectId), (prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((item) =>
                  item.id === input.id ? { ...item, ...optimisticInput } : item,
                ),
              }
            : prev,
        );
      }
      return { previousAll, previousProject, previousProjectId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject && context.previousProjectId) {
        queryClient.setQueryData(projectKey(context.previousProjectId), context.previousProject);
      }
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData<QueryResult<FeatureDto>>(allKey, (prev) =>
        upsertIntoResult(prev, updated),
      );
      queryClient.setQueryData<QueryResult<FeatureDto>>(projectKey(updated.projectId), (prev) =>
        upsertIntoResult(prev, updated),
      );
      await invalidateQueries([
        invalidateFeatures(undefined, orgKey),
        invalidateFeatures(updated.projectId, orgKey),
        invalidateFeatureDetail(updated.slug, orgKey, updated.projectId),
        invalidateGridQuery(gridPaths.features),
        invalidateReleases(undefined, orgKey),
        invalidateReleases(updated.projectId, orgKey),
        invalidateReleaseDetails(),
      ]);
    },
  });
};

export const useDeleteFeature = (): UseMutationResult<
  { id: string; projectId?: string },
  ApiError,
  DeleteFeatureInput,
  { previousAll?: QueryResult<FeatureDto>; previousProject?: QueryResult<FeatureDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  const allKey = queryKeys.features(undefined, orgKey);
  const projectKey = (projectId: string | undefined): ReturnType<typeof queryKeys.features> =>
    queryKeys.features(projectId ?? null, orgKey);

  return useMutation<
    { id: string; projectId?: string },
    ApiError,
    DeleteFeatureInput,
    { previousAll?: QueryResult<FeatureDto>; previousProject?: QueryResult<FeatureDto> }
  >({
    mutationFn: async (input: DeleteFeatureInput): Promise<{ id: string; projectId?: string }> => {
      const result = await deleteFeature(input, orgForQuery);
      return { ...result, projectId: input.projectId };
    },
    onMutate: (input) => {
      const previousAll = queryClient.getQueryData<QueryResult<FeatureDto>>(allKey);
      const previousProject = input.projectId
        ? queryClient.getQueryData<QueryResult<FeatureDto>>(projectKey(input.projectId))
        : undefined;
      queryClient.setQueryData<QueryResult<FeatureDto>>(allKey, (prev) =>
        removeFromResult(prev, input.id),
      );
      if (previousProject && input.projectId) {
        queryClient.setQueryData<QueryResult<FeatureDto>>(projectKey(input.projectId), (prev) =>
          removeFromResult(prev, input.id),
        );
      }
      return { previousAll, previousProject };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject) {
        queryClient.setQueryData(
          projectKey(context.previousProject.items[0]?.projectId),
          context.previousProject,
        );
      }
    },
    onSuccess: async (deleted) => {
      queryClient.setQueryData<QueryResult<FeatureDto>>(allKey, (prev) =>
        removeFromResult(prev, deleted.id),
      );
      if (deleted.projectId) {
        queryClient.setQueryData<QueryResult<FeatureDto>>(projectKey(deleted.projectId), (prev) =>
          removeFromResult(prev, deleted.id),
        );
      }
      await invalidateQueries([
        invalidateFeatures(undefined, orgKey),
        ...(deleted.projectId ? [invalidateFeatures(deleted.projectId, orgKey)] : []),
        invalidateGridQuery(gridPaths.features),
      ]);
    },
  });
};

export const useInvalidateFeatures = (): ((projectId?: string | null) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useCallback(
    async (projectId?: string | null): Promise<void> => {
      await invalidateQueries([
        invalidateFeatures(projectId, orgKey),
        invalidateGridQuery(gridPaths.features),
      ]);
    },
    [invalidateQueries, orgKey],
  );
};

export const useInvalidateFeatureDetail = (): ((
  slug: string | null,
  projectId?: string | null,
) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useCallback(
    async (slug: string | null, projectId?: string | null): Promise<void> => {
      await invalidateQueries([
        invalidateFeatureDetail(slug, orgKey, projectId ?? selectedProjectId ?? null),
      ]);
    },
    [invalidateQueries, orgKey, selectedProjectId],
  );
};
